import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiBook, FiClock, FiUsers, FiX, FiPlay, FiChevronRight } from 'react-icons/fi';
import { getRecipes } from '../utils/api';
import { getVideoInfo } from '../utils/helpers';
import { getRecipeImageUrl } from '../utils/imageUtils';

const Recipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Category states: 'recipes' (breakfast, lunch, dinner, snack) or 'detox' (empty_stomach, bed_time, anytime)
  const [activeCategory, setActiveCategory] = useState('recipes');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  // Pagination & Search States
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(8);

  const [heroRef, heroInView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    const fetchRecipes = async () => {
      try {
        const data = await getRecipes();
        console.log('🔍 Recipes data received:', data);
        setRecipes(data.recipes || []);
      } catch (error) {
        console.error('Failed to fetch recipes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecipes();
  }, []);

  // Reactive filtering
  useEffect(() => {
    let result = [...recipes];
    
    // 1. Filter by top-level category
    if (activeCategory === 'recipes') {
      result = result.filter(r => ['breakfast', 'lunch', 'dinner', 'snack'].includes(r.meal_type));
    } else {
      result = result.filter(r => ['empty_stomach', 'bed_time', 'anytime'].includes(r.meal_type));
    }
    
    // 2. Filter by sub-category
    if (activeFilter !== 'all') {
      result = result.filter(r => r.meal_type === activeFilter);
    }

    // 3. Filter by search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(r => 
        (r.title && r.title.toLowerCase().includes(query)) ||
        (r.description && r.description.toLowerCase().includes(query)) ||
        (r.ingredients && r.ingredients.toLowerCase().includes(query))
      );
    }
    
    setFilteredRecipes(result);
  }, [recipes, activeCategory, activeFilter, searchQuery]);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setActiveFilter('all'); // Reset sub-filter when category changes
    setSearchInput('');     // Reset search input text
    setSearchQuery('');     // Reset active search query
    setVisibleCount(8);     // Reset pagination
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setVisibleCount(8);     // Reset pagination to first page of results
  };

  const recipeFilters = [
    { key: 'all', label: 'All Recipes' },
    { key: 'breakfast', label: 'Breakfast' },
    { key: 'lunch', label: 'Lunch' },
    { key: 'dinner', label: 'Dinner' },
    { key: 'snack', label: 'Snacks' }
  ];

  const detoxFilters = [
    { key: 'all', label: 'All Detox Waters' },
    { key: 'empty_stomach', label: 'Empty Stomach' },
    { key: 'bed_time', label: 'Bed Time' },
    { key: 'anytime', label: 'Anytime' }
  ];

  const parseIngredients = (ingredientsString) => {
    try {
      return JSON.parse(ingredientsString);
    } catch {
      if (typeof ingredientsString === 'string') {
        return ingredientsString.split('\n').filter(item => item.trim());
      }
      return [];
    }
  };

  const getRecipeSchema = () => {
    if (!selectedRecipe) return null;
    const parsedIngredients = parseIngredients(selectedRecipe.ingredients);
    return {
      '@context': 'https://schema.org',
      '@type': 'Recipe',
      'name': selectedRecipe.title,
      'image': getRecipeImageUrl(selectedRecipe.image),
      'author': {
        '@type': 'Person',
        'name': 'Deeksha Rajput'
      },
      'description': selectedRecipe.description || `A delicious and healthy ${selectedRecipe.title} recipe by dietitian Deeksha Rajput.`,
      'recipeIngredient': parsedIngredients,
      'recipeInstructions': selectedRecipe.instructions ? selectedRecipe.instructions.split('\n').filter(Boolean).map(text => ({
        '@type': 'HowToStep',
        'text': text.trim()
      })) : []
    };
  };

  useSEO({
    title: selectedRecipe ? `${selectedRecipe.title} Recipe` : 'Healthy & Nutritious Diet Recipes',
    description: selectedRecipe 
      ? `Learn how to make ${selectedRecipe.title}. Ingredients: ${parseIngredients(selectedRecipe.ingredients).slice(0, 5).join(', ')}... and step-by-step instructions.` 
      : 'Browse Dfitclub\'s collection of healthy breakfast, lunch, dinner, snack, and detox recipes. Made with natural ingredients for easy weight management.',
    keywords: selectedRecipe 
      ? `${selectedRecipe.title} recipe, healthy cooking, dietitian recipe`
      : 'healthy recipes, diet food, weight loss recipes, breakfast ideas, detox water recipe, Dfitclub recipes',
    schema: getRecipeSchema()
  });

  // Helper to get YouTube Thumbnail
  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  // Modal Video Embed URL Generator
  const getEmbedUrl = (recipe) => {
    if (!recipe || !recipe.youtube_video_id) return '';
    const videoInfo = getVideoInfo(recipe.youtube_video_id);
    if (!videoInfo) return '';

    if (videoInfo.provider === 'instagram') {
      return `https://www.instagram.com/p/${videoInfo.id}/embed/`;
    }
    if (videoInfo.provider === 'facebook') {
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(videoInfo.id)}&show_text=0&t=0`;
    }
    // YouTube autoplay, mute, controls enabled, related videos disabled
    return `https://www.youtube.com/embed/${videoInfo.id}?autoplay=1&mute=1&controls=1&rel=0&fs=1&enablejsapi=1`;
  };

  return (
    <div className="min-h-screen bg-neutral-25">
      {/* Hero Section */}
      <section ref={heroRef} className="pt-20 pb-10 bg-gradient-to-br from-orange-50/60 via-accent-50/20 to-white overflow-hidden relative">
        <div className="absolute inset-0 z-0 bg-[radial-gradient(#fed7aa_1px,transparent_1px)] [background-size:24px_24px] opacity-35" />
        <div className="container-max relative z-10 px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-1.5 bg-orange-100 text-orange-700 text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              Dfitclub Kitchen
            </span>
            <h1 className="heading-hero text-gray-900 mb-6 font-bold leading-tight">
              Healthy & Taste-Packed <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-600">Recipes</span>
            </h1>
            <p className="text-lg text-secondary-600 mb-0 leading-relaxed">
              Delicious, chef-curated nutrition plans to feed your goals. Whether you're working on fat loss, muscle building, or general wellness, find the perfect meal here.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Top Category Tabs */}
      <section className="pt-4 pb-6 bg-white border-b border-gray-100 flex justify-center z-40 relative">
        <div className="bg-gray-100 p-1.5 rounded-full inline-flex shadow-inner border border-gray-200/50">
          <button
            onClick={() => handleCategoryChange('recipes')}
            className={`px-8 py-3 rounded-full text-sm md:text-base font-bold transition-all duration-300 ${
              activeCategory === 'recipes'
                ? 'bg-white text-orange-500 shadow-md scale-105'
                : 'text-secondary-600 hover:text-orange-50'
            }`}
          >
            Healthy Recipes
          </button>
          <button
            onClick={() => handleCategoryChange('detox')}
            className={`px-8 py-3 rounded-full text-sm md:text-base font-bold transition-all duration-300 ${
              activeCategory === 'detox'
                ? 'bg-white text-orange-500 shadow-md scale-105'
                : 'text-secondary-600 hover:text-orange-50'
            }`}
          >
            Detox Waters
          </button>
        </div>
      </section>

      {/* Sub-Filter and Search Section */}
      <section className="sticky top-20 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm py-4">
        <div className="container-max px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Sub-Filters */}
            <div className="flex overflow-x-auto no-scrollbar scroll-smooth gap-3 py-2 -mx-4 px-4 justify-start w-full md:w-auto">
              {(activeCategory === 'recipes' ? recipeFilters : detoxFilters).map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => {
                    setActiveFilter(filter.key);
                    setVisibleCount(8);
                  }}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 flex-shrink-0 ${
                    activeFilter === filter.key
                      ? 'bg-orange-500 text-white shadow-md shadow-orange-500/30 scale-105'
                      : 'bg-gray-100 text-secondary-700 hover:bg-orange-50 hover:text-orange-600'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Search Input and Button */}
            <form onSubmit={handleSearchSubmit} className="flex w-full md:w-80 relative items-center gap-2">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder={`Search ${activeCategory === 'recipes' ? 'recipes' : 'detox waters'}...`}
                  value={searchInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchInput(value);
                    setSearchQuery(value);
                    setVisibleCount(8);
                  }}
                  className="w-full pl-4 pr-10 py-2.5 border border-neutral-200 rounded-full focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm bg-gray-50/50"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput('');
                      setSearchQuery('');
                      setVisibleCount(8);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full text-sm transition-all duration-300 shadow-md shadow-orange-500/20"
              >
                Search
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Recipes Grid */}
      <section id="recipes-grid" className="py-16 bg-neutral-25">
        <div className="container-max px-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 h-96 animate-pulse">
                  <div className="h-52 bg-gray-200" />
                  <div className="p-5 space-y-3">
                    <div className="h-6 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-full" />
                    <div className="h-4 bg-gray-200 rounded w-5/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredRecipes.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100 max-w-xl mx-auto">
              <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
                🍳
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-2">No Items Found</h3>
              <p className="text-secondary-600 max-w-sm mx-auto">
                {activeCategory === 'recipes'
                  ? "We are cooking up new recipes! Check back later."
                  : "Refreshing detox waters are on the way! Check back later."}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filteredRecipes.slice(0, visibleCount).map((recipe, index) => {
                const videoInfo = recipe.youtube_video_id ? getVideoInfo(recipe.youtube_video_id) : null;
                const hasUploadedImage = recipe.image_url || recipe.image;
                
                // Fallback thumbnail resolve
                let cardImage = null;
                if (hasUploadedImage) {
                  cardImage = getRecipeImageUrl(recipe.image_url || recipe.image);
                } else if (videoInfo) {
                  if (videoInfo.provider === 'youtube') {
                    cardImage = getYouTubeThumbnail(videoInfo.id);
                  } else if (videoInfo.provider === 'instagram') {
                    cardImage = `https://www.instagram.com/p/${videoInfo.id}/media/?size=l`;
                  }
                }

                const hasImageError = imageErrors[recipe.id];

                return (
                  <motion.div
                    key={recipe.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    whileHover={{ y: -8, transition: { duration: 0.3 } }}
                    onClick={() => setSelectedRecipe(recipe)}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 transition-all duration-300 flex flex-col h-full cursor-pointer group"
                  >
                    {/* Image Header */}
                    <div className="relative h-52 overflow-hidden bg-orange-50">
                      {cardImage && !hasImageError ? (
                        <motion.img
                          src={cardImage}
                          alt={recipe.title}
                          onError={() => setImageErrors(prev => ({ ...prev, [recipe.id]: true }))}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-4xl bg-gradient-to-br from-orange-50 to-amber-100 text-orange-500">
                          🥗
                        </div>
                      )}

                      {/* Play Button Overlay */}
                      {recipe.youtube_video_id && (
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex items-center justify-center shadow-lg transform scale-90 group-hover:scale-100 transition-transform duration-300">
                            <FiPlay className="w-5 h-5 ml-0.5 fill-current" />
                          </div>
                        </div>
                      )}

                    </div>

                    {/* Card Body */}
                    <div className="p-5 flex flex-col flex-grow">
                      {/* Badges */}
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-[10px] font-bold uppercase rounded-full tracking-wider">
                          {recipe.meal_type.replace('_', ' ')}
                        </span>
                        {recipe.goal_type && (
                          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full tracking-wider ${
                            recipe.goal_type === 'weight_loss'
                              ? 'bg-blue-100 text-blue-800'
                              : recipe.goal_type === 'weight_gain'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {recipe.goal_type === 'weight_loss' ? 'Weight Loss' : 
                             recipe.goal_type === 'weight_gain' ? 'Weight Gain' : 'Wellness'}
                          </span>
                        )}
                      </div>

                      <h3 className="text-lg font-bold text-gray-900 line-clamp-1 mb-2 group-hover:text-orange-500 transition-colors">
                        {recipe.title}
                      </h3>
                      
                      <p className="text-secondary-600 text-sm line-clamp-2 mb-4 leading-relaxed flex-grow">
                        {recipe.description || 'View details, cooking instructions, and ingredient breakdown for this healthy recipe.'}
                      </p>

                      {/* Metrics bar */}
                      <div className="flex items-center justify-between text-xs text-secondary-500 pt-3 border-t border-gray-100 font-medium">
                        <span className="flex items-center gap-1">
                          <FiClock className="w-3.5 h-3.5 text-gray-400" />
                          {recipe.total_time || (parseInt(recipe.prep_time || 0) + parseInt(recipe.cook_time || 0)) || 15} mins
                        </span>
                        <span className="flex items-center gap-1">
                          <FiUsers className="w-3.5 h-3.5 text-gray-400" />
                          {recipe.servings || 1} Servings
                        </span>
                        {recipe.calories_per_serving && (
                          <span className="px-2 py-0.5 bg-orange-50 text-orange-700 font-bold rounded">
                            {recipe.calories_per_serving} kcal
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {visibleCount < filteredRecipes.length && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={() => setVisibleCount(prev => prev + 8)}
                  className="px-8 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-orange-500/25 transition-all duration-300 hover:scale-105 flex items-center gap-2"
                >
                  See More
                  <FiChevronRight className="w-5 h-5 rotate-90" />
                </button>
              </div>
            )}
          </>
          )}
        </div>
      </section>

      {/* Immersive Recipe Details Modal */}
      <AnimatePresence>
        {selectedRecipe && (() => {
          const videoInfo = selectedRecipe.youtube_video_id ? getVideoInfo(selectedRecipe.youtube_video_id) : null;
          const isInstagram = videoInfo && videoInfo.provider === 'instagram';
          const isFacebook = videoInfo && videoInfo.provider === 'facebook';
          const isVertical = isInstagram || isFacebook;
          
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedRecipe(null)}
                className="absolute inset-0 bg-neutral-900/80 backdrop-blur-md"
              />

              {/* Modal Body */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="bg-white rounded-3xl overflow-hidden shadow-2xl max-w-5xl w-full max-h-[90vh] md:max-h-[85vh] flex flex-col md:flex-row relative z-10"
              >
                {/* Close Button */}
                <button
                  onClick={() => setSelectedRecipe(null)}
                  className="absolute top-4 right-4 z-50 p-2.5 rounded-full bg-white/90 backdrop-blur-sm text-neutral-800 hover:bg-orange-500 hover:text-white shadow transition-all duration-200"
                >
                  <FiX className="w-5 h-5" />
                </button>

                {/* Left Column: Video Embed / Cover Image */}
                <div className="w-full md:w-[55%] bg-neutral-950 flex items-center justify-center relative aspect-video md:aspect-auto min-h-[350px] md:min-h-[500px] p-4">
                  {selectedRecipe.youtube_video_id ? (
                    isVertical ? (
                      /* Styled centered vertical container for Instagram Reels & Facebook Reels */
                      <div className="w-full max-w-[310px] h-full max-h-[500px] aspect-[9/16] bg-neutral-900 rounded-2xl overflow-hidden shadow-2xl relative border border-neutral-800 flex items-center justify-center">
                        <iframe
                          src={getEmbedUrl(selectedRecipe)}
                          title={selectedRecipe.title}
                          className="w-full h-full absolute inset-0"
                          frameBorder="0"
                          scrolling="no"
                          allowTransparency="true"
                          allow="autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          style={{ border: 'none', overflow: 'hidden' }}
                        />
                      </div>
                    ) : (
                      /* Full landscape view for YouTube */
                      <iframe
                        src={getEmbedUrl(selectedRecipe)}
                        title={selectedRecipe.title}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    )
                  ) : (
                    <img
                      src={selectedRecipe.image_url || selectedRecipe.image ? getRecipeImageUrl(selectedRecipe.image_url || selectedRecipe.image) : ''}
                      alt={selectedRecipe.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Right Column: Recipe Details */}
                <div className="w-full md:w-[45%] p-6 md:p-8 overflow-y-auto flex flex-col h-full max-h-[50vh] md:max-h-[85vh] scrollbar-thin">
                  <div className="flex gap-2 mb-3">
                    <span className="px-2.5 py-1 bg-orange-100 text-orange-800 text-xs font-bold rounded-full uppercase tracking-wider">
                      {selectedRecipe.meal_type.replace('_', ' ')}
                    </span>
                    {selectedRecipe.goal_type && (
                      <span className="px-2.5 py-1 bg-orange-500 text-white text-xs font-bold rounded-full uppercase tracking-wider">
                        {selectedRecipe.goal_type.replace('_', ' ')}
                      </span>
                    )}
                  </div>

                  <h2 className="text-2xl md:text-3xl font-extrabold text-neutral-900 leading-tight mb-3">
                    {selectedRecipe.title}
                  </h2>

                  <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                    {selectedRecipe.description || 'This healthy dish fits perfectly into a well-balanced training or weight control regimen.'}
                  </p>

                  {/* Info Pills */}
                  <div className="grid grid-cols-4 gap-3 text-center border-t border-b border-gray-100 py-4 mb-6 bg-neutral-50/50 rounded-xl p-3">
                    <div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1 font-semibold">Prep</div>
                      <div className="font-bold text-neutral-900">{selectedRecipe.prep_time || 5}m</div>
                    </div>
                    <div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1 font-semibold">Cook</div>
                      <div className="font-bold text-neutral-900">{selectedRecipe.cook_time || 10}m</div>
                    </div>
                    <div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1 font-semibold">Servings</div>
                      <div className="font-bold text-neutral-900">{selectedRecipe.servings || 1}</div>
                    </div>
                    <div>
                      <div className="text-xs text-orange-500 uppercase tracking-wider mb-1 font-bold">Calories</div>
                      <div className="font-extrabold text-orange-600">{selectedRecipe.calories_per_serving || 'N/A'}</div>
                    </div>
                  </div>

                  {/* Ingredients */}
                  <div className="mb-6">
                    <h4 className="text-lg font-bold text-neutral-900 mb-3 flex items-center gap-2">
                      <span className="text-xl">🛒</span> Ingredients
                    </h4>
                    <ul className="space-y-2">
                      {parseIngredients(selectedRecipe.ingredients).map((ing, idx) => (
                        <li key={idx} className="text-sm text-neutral-700 flex items-start">
                          <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 mr-2.5 flex-shrink-0" />
                          <span>{ing}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Instructions */}
                  <div>
                    <h4 className="text-lg font-bold text-neutral-900 mb-3 flex items-center gap-2">
                      <span className="text-xl">📝</span> Instructions
                    </h4>
                    <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line bg-orange-50/30 border border-orange-100/50 rounded-xl p-4">
                      {selectedRecipe.instructions}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          );
        })()}
      </AnimatePresence>

      {/* Cooking Tips Section */}
      <section className="py-20 bg-white border-t border-gray-100 relative">
        <div className="container-max px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-4">
              Smart Kitchen <span className="text-orange-500">Hacks</span>
            </h2>
            <p className="text-secondary-600 max-w-xl mx-auto">
              Follow these simple lifestyle guidelines to maximize taste, health, and prep speed.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Meal Prep Like a Pro",
                description: "Prepare ingredients in advance to save time and ensure you stick to your nutrition goals throughout the week.",
                icon: "🥘"
              },
              {
                title: "Portion Control",
                description: "Use measuring cups and a kitchen scale to ensure accurate portions that align with your health goals.",
                icon: "⚖️"
              },
              {
                title: "Fresh Ingredients",
                description: "Choose fresh, seasonal ingredients for the best flavor and maximum nutritional value in your meals.",
                icon: "🥬"
              },
              {
                title: "Healthy Substitutions",
                description: "Learn simple swaps like using Greek yogurt instead of sour cream or cauliflower rice instead of regular rice.",
                icon: "🔄"
              },
              {
                title: "Spice It Up",
                description: "Use herbs and spices instead of excess salt or sugar to add flavor while keeping your meals healthy.",
                icon: "🌿"
              },
              {
                title: "Stay Hydrated",
                description: "Drink plenty of water throughout the day and consider herbal teas as healthy alternatives to sugary drinks.",
                icon: "💧"
              }
            ].map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-neutral-50 hover:bg-white border border-gray-100 hover:shadow-xl rounded-2xl p-6 text-center transition-all duration-300"
              >
                <div className="text-3xl mb-3">{tip.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{tip.title}</h3>
                <p className="text-secondary-600 text-sm leading-relaxed">{tip.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-amber-500 text-white overflow-hidden relative">
        <div className="absolute inset-0 z-0 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px] opacity-10" />
        <div className="container-max relative z-10 px-4 text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h2 className="text-3xl md:text-5xl font-extrabold leading-tight">
              Looking for a Custom Meal Plan?
            </h2>
            <p className="text-lg text-orange-50 leading-relaxed max-w-2xl mx-auto">
              Get dietitian-designed nutrition plans tailored exclusively to your daily macro targets, eating preferences, and schedules.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Link to="/contact" className="px-8 py-4 bg-white text-orange-600 font-bold rounded-xl shadow-lg hover:bg-orange-50 hover:scale-105 transition-all duration-300">
                Get Custom Meal Plan
              </Link>
              <Link to="/services" className="px-8 py-4 bg-orange-600 border border-orange-400 font-bold rounded-xl hover:bg-orange-700 hover:scale-105 transition-all duration-300">
                Explore Packages
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Recipes;
